import { useLazyQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ObjectUtils } from "../Utils/ObjectUtils";
import { getRiskAssessmentFactorList } from "../sharedCode/queries";
import { RiskAssessmentFactor } from "../sharedCode/avroGraphql";
import { buildRiskAssessmentFactorSelectName, RiskAssessmentFactorExtended } from "../Utils/TypeExtensions";
import { BackgroundProps, SyncStatus, TableSyncer } from "../Utils/Interfaces";

export default function useRiskAssessmentFactorBackgroundPull(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Loading);
    const [doQuery, { loading: gqlLoading, error: gqlError, data: gqlResult, refetch: refetch }] = useLazyQuery(getRiskAssessmentFactorList, {
        fetchPolicy: "cache-and-network",
    });

    const fetchData = (firstRun: boolean) => {
        if (firstRun) {
            doQuery();
        } else {
            setStatus(SyncStatus.Loading);
            props.updateUi();
            refetch()
                .then((results) => {
                    const rawList = results.data.getRiskAssessmentFactors.slice() as RiskAssessmentFactor[];
                    saveToLocalDatabase(rawList);
                    setStatus(SyncStatus.Fresh);
                    props.updateUi();
                })
                .catch((e) => {
                    setStatus(SyncStatus.Stale);
                    props.updateUi();
                });
        }
    };

    function saveToLocalDatabase(rawList: RiskAssessmentFactor[]) {
        const listToSave = ObjectUtils.convertToUppercase(
            rawList.map((r: RiskAssessmentFactor) => {
                return { ...r, selectName: buildRiskAssessmentFactorSelectName(r, rawList) };
            })
        ) as RiskAssessmentFactorExtended[];

        TicketDatabase.transaction("rw", TicketDatabase.riskAssessmentFactor, async () => {
            await TicketDatabase.riskAssessmentFactor
                ?.toCollection()
                .primaryKeys()
                .then((keys) => {
                    TicketDatabase.riskAssessmentFactor.bulkDelete(keys);
                })
                .then(() => TicketDatabase.riskAssessmentFactor.bulkAdd(listToSave));
        });
    }

    useEffect(() => {
        if (gqlResult) {
            const rawList = gqlResult.getRiskAssessmentFactors.slice();
            saveToLocalDatabase(rawList);
            setStatus(SyncStatus.Fresh);
            props.updateUi();
        }
    }, [gqlResult]);

    useEffect(() => {
        if (gqlError) {
            TicketDatabase.riskAssessmentFactor.count().then((c) => {
                setStatus(c > 0 ? SyncStatus.Stale : SyncStatus.Error);
                props.updateUi();
            });
        }
    }, [gqlError]);

    useEffect(() => {
        if (gqlLoading) {
            setStatus(SyncStatus.Loading);
            props.updateUi();
        }
    }, [gqlLoading]);

    return { status, fetchData: fetchData };
}
